import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getConfig } from 'config';
import Lang from 'types/Lang';
import kioskSessionService from 'services/kiosk-session-service';
import logError from 'utils/log-error';

const supportedLngs = Object.values(Lang);

const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'primaryNavigator',
  lookup: () => {
    if (typeof navigator === 'undefined') return undefined;
    const primary = navigator.languages?.[0] ?? navigator.language;
    return primary ? primary.split('-')[0] : undefined;
  },
});

const options = {
  supportedLngs,
  fallbackLng: Lang.nl,
  load: 'languageOnly' as const,
  detection: {
    order: ['localStorage', 'primaryNavigator'],
    caches: [],
    convertDetectedLanguage: (lng: string) => lng.split('-')[0],
  },
  react: {
    useSuspense: false,
  },
  interpolation: {
    escapeValue: false,
    // Backend-managed translation values use single-brace placeholders (e.g. "{count}"),
    // not i18next's default "{{count}}".
    prefix: '{',
    suffix: '}',
  },
};

export const initTranslations = async (): Promise<void> => {
  try {
    i18next.use(initReactI18next);
    i18next.use(languageDetector);
    i18next.use(HttpApi);

    // A reloading kiosk starts straight in its own language with every language loaded, instead of the
    // browser language followed by one or two visible switches.
    const kioskLanguage = kioskSessionService.getStartupLanguage();
    await i18next.init({
      ...options,
      ...(kioskLanguage ? { lng: kioskLanguage, preload: supportedLngs } : {}),
      backend: {
        loadPath: `${getConfig('API_ENDPOINT')}Translations/GetTranslations/{lng}`,
        customHeaders: () => ({
          'X-Neembu-Agency-Slug': window.location.hostname,
        }),
        requestOptions: () => ({
          headers: {
            'X-Neembu-Agency-Slug': window.location.hostname,
          },
        }),
      },
    });
  } catch (err) {
    logError(err);
  }
};

/**
 * Fetch every supported language up front (the kiosk), so switching language afterwards is instant
 * and still works offline. A failure is harmless: a missing language is fetched on first switch.
 */
export const preloadAllLanguages = async (): Promise<void> => {
  try {
    await i18next.loadLanguages(supportedLngs);
  } catch (err) {
    logError(err);
  }
};
